import { Component, Input, Output, EventEmitter, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { PermitCategoryService } from '../../services/permit-category.service';
import { PermitStatusService } from '../../services/permit-status.service';
import { PermitDepartmentService } from '../../services/permit-department.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { CreatePermitTypePayload, FormFieldConfig, PermitTypeConfig, displayUnitFor } from '../../models/permit-type.model';
import { PermitFormFieldDialogComponent } from '../dialogs/permit-form-field-dialog/permit-form-field-dialog.component';
import { PermitDepartment, permitDepartmentErrorKey, permitDepartmentName } from '../../models/permit-department.model';
import { PermitStatus } from '../../models/permit-status.model';
import { ApplicantType } from '../../constants/permit.constants';
import { I18nString } from '../../models/permit-category.model';
import { PermitConfigRefreshService } from '../../services/permit-config-refresh.service';
import { PermitSettingsService } from '../../services/permit-settings.service';
import { PermitFile, isPermitFileExcel, isPermitFilePdf, isPermitFileWord, permitFileDescription, permitFileName, safePermitFileUrl } from '../../models/permit.model';

interface SelectOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-permit-type-editor',
    templateUrl: './permit-type-editor.component.html',
    styleUrls: ['./permit-type-editor.component.scss'],
})
export class PermitTypeEditorComponent implements OnInit, OnChanges, OnDestroy {
    @Input() categoryId: string;
    @Input() typeId: string | null = null;

    @Output() saved = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    editingType: PermitTypeConfig | null = null;
    loading = false;
    saving = false;

    form: FormGroup | null = null;
    structureDirty = false;

    // When true, the citizen form is owned by the external-system sync and is read-only here.
    // Set from the backend on load; not editable from the admin console (the sync overwrites an
    // imported type's form on every run regardless, so a local "disconnect and edit" would only
    // lose work on the next sync).
    connected = false;

    formFields: FormFieldConfig[] = [];
    // Template/resource files synced from the external system. Read-only, never sent back in buildPayload.
    typeFiles: PermitFile[] = [];
    // Admin-set display units keyed by fieldId, each an { fr, en } pair. Kept outside
    // formFields/structureDirty so it survives even on connected (sync-locked) types — see
    // PermitTypeConfig.fieldUnits.
    fieldUnits: Record<string, I18nString> = {};
    fieldUnitsDirty = false;

    // Org-level: when the permit system is connected, the external system defines the workflow,
    // so which statuses a type uses is not ours to pick. Distinct from `connected`, which is
    // per-type and only locks the citizen form.
    statusesLocked = false;

    departments: PermitDepartment[] = [];
    departmentOptions: SelectOption[] = [];
    allStatuses: PermitStatus[] = [];
    applicantTypes = new Set<ApplicantType>();
    // Third-party (ApplicantType.OTHER) is intentionally not offered in the editor.
    readonly applicantTypeOptions: ApplicantType[] = [ApplicantType.SELF, ApplicantType.FAMILY];

    predefinedTasks: string[] = [];
    newTask = '';
    applicantTypesError = false;
    feeAmountError = false;
    externalPaymentMessageError = false;

    feeTypeOptions: SelectOption[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private categoryService: PermitCategoryService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private fb: FormBuilder,
        private departmentService: PermitDepartmentService,
        private statusService: PermitStatusService,
        private refreshService: PermitConfigRefreshService,
        private settingsService: PermitSettingsService,
    ) {}

    ngOnInit(): void {
        this.loadReferenceData();
        this.buildForm();
        if (this.typeId) {
            this.loadType();
        }
        this.settingsService.isExternallyManaged$.pipe(takeUntil(this.destroy$)).subscribe((connected) => {
            this.statusesLocked = connected;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.categoryId || changes.typeId) {
            const isFirstChange = (changes.categoryId?.firstChange ?? true) && (changes.typeId?.firstChange ?? true);
            if (!isFirstChange) {
                this.editingType = null;
                this.structureDirty = false;
                this.buildForm();
                if (this.typeId) {
                    this.loadType();
                }
            }
        }
    }

    private loadType(): void {
        this.loading = true;
        this.categoryService
            .getCategoryEdition(this.categoryId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (edition) => {
                    const types = (edition.permitTypes || []) as unknown as PermitTypeConfig[];
                    this.editingType = types.find((t) => t._id === this.typeId) || null;
                    if (this.editingType) {
                        this.prefill(this.editingType);
                    }
                    this.loading = false;
                    if (!this.editingType) {
                        this.messageService.error(this.translate.instant('permit.config.common.error'));
                        this.cancel.emit();
                    }
                },
                error: () => {
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                    this.cancel.emit();
                },
            });
    }

    private loadReferenceData(): void {
        this.departmentService
            .getDepartments({ includeInactive: false })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (departments) => {
                    this.departments = departments || [];
                    const lang = this.translate.currentLang || 'fr';
                    this.departmentOptions = this.departments.map((d) => ({ value: d._id, label: permitDepartmentName(d.name, lang) }));
                },
                error: () => this.messageService.error(this.translate.instant('permit.config.common.error')),
            });
        this.statusService
            .getAllStatuses(true)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (statuses) => (this.allStatuses = (statuses || []).slice().sort((a, b) => a.displayOrder - b.displayOrder)),
                error: () => this.messageService.error(this.translate.instant('permit.config.common.error')),
            });
        this.feeTypeOptions = [
            { value: 'fixed', label: this.translate.instant('permit.config.fees.type_fixed') },
            { value: 'informational', label: this.translate.instant('permit.config.fees.type_informational') },
        ];
    }

    private buildForm(): void {
        this.form = this.fb.group({
            nameFr: ['', Validators.required],
            nameEn: ['', Validators.required],
            numberPrefix: ['', [Validators.required, Validators.pattern(/^[A-Z]{2,4}$/)]],
            defaultDepartment: ['', Validators.required],
            validityDurationDays: [null],
            active: [true],
            statuses: [[]],
            feeEnabled: [false],
            feeTiming: ['upfront'],
            feeType: [null],
            feeAmount: [null],
            feeDescription: [''],
            externalPaymentEnabled: [false],
            externalPaymentMessageFr: [''],
            externalPaymentMessageEn: [''],
        });
        this.applicantTypes = new Set<ApplicantType>();
        this.formFields = [];
        this.typeFiles = [];
        this.fieldUnits = {};
        this.fieldUnitsDirty = false;
        this.predefinedTasks = [];
        this.applicantTypesError = false;
        this.feeAmountError = false;
        this.externalPaymentMessageError = false;
        this.connected = false;
    }

    /** True when the citizen form is owned by the external system and read-only here. */
    get isFormLocked(): boolean {
        return this.connected === true;
    }

    get isInformationalFee(): boolean {
        return this.selectValue(this.form.value.feeType) === 'informational';
    }

    private prefill(type: PermitTypeConfig): void {
        this.form.patchValue({
            nameFr: (type.name && type.name.fr) || '',
            nameEn: (type.name && type.name.en) || '',
            numberPrefix: type.numberPrefix || '',
            defaultDepartment: this.refId(type.defaultDepartment),
            validityDurationDays: type.validityDurationDays != null ? type.validityDurationDays : null,
            active: type.active !== false,
            statuses: (type.statuses || []).map((s: any) => this.refId(s)),
        });
        this.connected = type.connected === true;
        this.applicantTypes = new Set((type.applicantTypes || []) as ApplicantType[]);
        this.formFields = (type.formFields || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
        this.typeFiles = type.files || [];
        this.fieldUnits = { ...(type.fieldUnits || {}) };
        this.fieldUnitsDirty = false;
        this.editingUnitFieldIds.clear();
        this.predefinedTasks = (type.predefinedTasks || []).map((t: any) => (typeof t === 'string' ? t : t.title));
        if (type.feeConfig) {
            const description = type.feeConfig.description as any;
            const descriptionText = typeof description === 'string' ? description : description?.fr || description?.en || '';
            this.form.patchValue({
                feeEnabled: true,
                feeTiming: type.feeConfig.timing || 'upfront',
                feeType: type.feeConfig.type || null,
                feeAmount: type.feeConfig.amount != null ? type.feeConfig.amount : null,
                feeDescription: descriptionText,
            });
        }
        if (type.externalPayment) {
            this.form.patchValue({
                externalPaymentEnabled: type.externalPayment.enabled === true,
                externalPaymentMessageFr: type.externalPayment.message?.fr || '',
                externalPaymentMessageEn: type.externalPayment.message?.en || '',
            });
        }
    }

    refId(ref: any): string {
        if (!ref) return '';
        return typeof ref === 'string' ? ref : ref._id;
    }

    /**
     * SelectV2Component stores the whole option object ({ value, label }) in the
     * form control, so unwrap it to the scalar value the API expects.
     */
    private selectValue(val: any): any {
        if (val && typeof val === 'object') {
            return val.value != null ? val.value : val._id;
        }
        return val;
    }

    toggleApplicantType(type: ApplicantType): void {
        if (this.applicantTypes.has(type)) {
            this.applicantTypes.delete(type);
        } else {
            this.applicantTypes.add(type);
        }
        this.form.markAsDirty();
    }

    statusName(status: PermitStatus): string {
        const name = status.name as I18nString;
        if (!name) return '';
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    isStatusChecked(statusId: string): boolean {
        const statuses: string[] = this.form?.value?.statuses || [];
        return statuses.includes(statusId);
    }

    toggleStatus(statusId: string): void {
        if (this.statusesLocked) return;
        const current: string[] = (this.form?.value?.statuses || []).slice();
        const idx = current.indexOf(statusId);
        if (idx === -1) {
            current.push(statusId);
        } else {
            current.splice(idx, 1);
        }
        this.form.patchValue({ statuses: current });
        this.form.markAsDirty();
    }

    // Each input shows exactly its own stored language, no fallback — the fr/en fallback in
    // displayUnitFor is for citizen-facing display only, not for editing raw values here.
    unitFr(field: FormFieldConfig): string {
        return this.fieldUnits[field.fieldId]?.fr || '';
    }

    unitEn(field: FormFieldConfig): string {
        return this.fieldUnits[field.fieldId]?.en || '';
    }

    // Compact read-only summary shown next to the pen icon, e.g. "pi² / ft²", or just "$" when
    // both languages are the same. Falls back to the '$' currency default (language-invariant)
    // only when nothing has been explicitly set — the edit inputs themselves stay raw/empty.
    unitSummary(field: FormFieldConfig): string {
        const fr = this.unitFr(field);
        const en = this.unitEn(field);
        if (!fr && !en) return displayUnitFor(field, this.fieldUnits, 'fr') || '';
        if (fr === en) return fr;
        return [fr, en].filter(Boolean).join(' / ');
    }

    private editingUnitFieldIds = new Set<string>();

    isEditingUnit(field: FormFieldConfig): boolean {
        return this.editingUnitFieldIds.has(field.fieldId);
    }

    toggleUnitEdit(field: FormFieldConfig): void {
        if (this.editingUnitFieldIds.has(field.fieldId)) {
            this.editingUnitFieldIds.delete(field.fieldId);
        } else {
            this.editingUnitFieldIds.add(field.fieldId);
        }
    }

    onUnitChange(field: FormFieldConfig, lang: 'fr' | 'en', value: string): void {
        const trimmed = (value || '').trim();
        const current = this.fieldUnits[field.fieldId] || { fr: '', en: '' };
        const updated: I18nString = { ...current, [lang]: trimmed };
        if (updated.fr || updated.en) {
            this.fieldUnits[field.fieldId] = updated;
        } else {
            delete this.fieldUnits[field.fieldId];
        }
        this.fieldUnitsDirty = true;
    }

    typeFileName(file: PermitFile): string {
        return permitFileName(file);
    }

    typeFileDescription(file: PermitFile): string {
        return permitFileDescription(file, this.translate.currentLang || 'fr');
    }

    isTypeFilePdf(file: PermitFile): boolean {
        return isPermitFilePdf(file);
    }

    isTypeFileWord(file: PermitFile): boolean {
        return isPermitFileWord(file);
    }

    isTypeFileExcel(file: PermitFile): boolean {
        return isPermitFileExcel(file);
    }

    openTypeFile(file: PermitFile): void {
        const url = safePermitFileUrl(file);
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    fieldLabel(field: FormFieldConfig): string {
        const { label } = field;
        if (!label) return '';
        const lang = this.translate.currentLang || 'fr';
        return label[lang] || label.fr || label.en || '';
    }

    openFieldDialog(field?: FormFieldConfig): void {
        if (this.isFormLocked) return;
        const dialogRef = this.dialog.open(PermitFormFieldDialogComponent, {
            width: '560px',
            data: {
                field: field || null,
                existingIds: this.formFields.map((f) => f.fieldId),
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: FormFieldConfig | undefined) => {
                if (!result) return;
                if (field) {
                    const index = this.formFields.indexOf(field);
                    this.formFields[index] = result;
                } else {
                    this.formFields.push(result);
                }
                this.structureDirty = true;
            });
    }

    dropField(event: CdkDragDrop<FormFieldConfig[]>): void {
        if (this.isFormLocked) return;
        if (event.previousIndex === event.currentIndex) return;
        moveItemInArray(this.formFields, event.previousIndex, event.currentIndex);
        this.structureDirty = true;
    }

    removeField(field: FormFieldConfig): void {
        if (this.isFormLocked) return;
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.form_builder.delete_title'),
                    body: this.translate.instant('permit.config.form_builder.delete_body'),
                    confirmLabel: this.translate.instant('permit.config.common.delete'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                const index = this.formFields.indexOf(field);
                if (index !== -1) {
                    this.formFields.splice(index, 1);
                    this.structureDirty = true;
                }
            });
    }

    isDirty(): boolean {
        return (this.form && this.form.dirty) || this.structureDirty || this.fieldUnitsDirty;
    }

    requestClose(): void {
        if (!this.isDirty()) {
            this.cancel.emit();
            return;
        }
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.editor.dirty_title'),
                    body: this.translate.instant('permit.config.editor.dirty_body'),
                    confirmLabel: this.translate.instant('permit.config.editor.dirty_confirm'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.cancel.emit();
                }
            });
    }

    addTask(): void {
        const title = (this.newTask || '').trim();
        if (!title) return;
        this.predefinedTasks.push(title);
        this.newTask = '';
        this.structureDirty = true;
    }

    removeTask(index: number): void {
        this.predefinedTasks.splice(index, 1);
        this.structureDirty = true;
    }

    private buildPayload(): CreatePermitTypePayload {
        const v = this.form.value;
        const payload: CreatePermitTypePayload = {
            name: { fr: v.nameFr.trim(), en: v.nameEn.trim() },
            numberPrefix: v.numberPrefix,
            defaultDepartment: this.selectValue(v.defaultDepartment),
            applicantTypes: Array.from(this.applicantTypes),
            active: v.active,
            predefinedTasks: this.predefinedTasks.map((title) => ({ title })),
        };
        // The backend rejects the presence of formFields on a connected (externally-managed)
        // type with 400 `permit.type-form-locked`. Omit it entirely while connected; the sync
        // owns the form. `connected` itself is backend/ops-owned and is not sent from here.
        if (!this.connected) {
            payload.formFields = this.formFields.map((field, index) => ({ ...field, displayOrder: index }));
        }
        // fieldUnits lives outside formFields and is never touched by the form-lock check, so it
        // can always be sent, even on connected types — that's the whole point of storing it here.
        payload.fieldUnits = this.fieldUnits;
        if (v.validityDurationDays != null && v.validityDurationDays !== '') {
            payload.validityDurationDays = Number(v.validityDurationDays);
        }
        if (v.statuses && v.statuses.length) {
            payload.statuses = v.statuses;
        }
        if (v.feeEnabled) {
            const feeType = this.selectValue(v.feeType);
            const feeConfig: any = { timing: v.feeTiming };
            if (feeType) feeConfig.type = feeType;
            // 'fixed' fees carry an amount; 'informational' fees carry citizen-facing free text.
            // description must be an i18n object ({ fr, en }) like every other display-text field.
            if (feeType === 'fixed') {
                if (v.feeAmount != null && v.feeAmount !== '') feeConfig.amount = Number(v.feeAmount);
            } else if (feeType === 'informational') {
                const description = (v.feeDescription || '').trim();
                if (description) feeConfig.description = { fr: description, en: description };
            }
            payload.feeConfig = feeConfig;
        }
        // Always sent so turning the toggle off is persisted (backend skips undefined fields).
        const messageFr = (v.externalPaymentMessageFr || '').trim();
        const messageEn = (v.externalPaymentMessageEn || '').trim();
        payload.externalPayment = {
            enabled: !!v.externalPaymentEnabled,
            ...(messageFr && messageEn ? { message: { fr: messageFr, en: messageEn } } : {}),
        };
        return payload;
    }

    save(): void {
        if (this.saving) return;
        this.form.markAllAsTouched();
        this.applicantTypesError = this.applicantTypes.size === 0;
        const v = this.form.value;
        const feeType = this.selectValue(v.feeType);
        this.feeAmountError = v.feeEnabled && feeType === 'fixed' && (v.feeAmount == null || v.feeAmount === '');
        this.externalPaymentMessageError = v.externalPaymentEnabled && (!(v.externalPaymentMessageFr || '').trim() || !(v.externalPaymentMessageEn || '').trim());
        if (this.form.invalid || this.applicantTypesError || this.feeAmountError || this.externalPaymentMessageError) return;

        const payload = this.buildPayload();
        const request$ = this.typeId ? this.categoryService.updatePermitType(this.categoryId, this.typeId, payload) : this.categoryService.createPermitType(this.categoryId, payload);

        this.saving = true;
        request$.pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
                this.saving = false;
                this.messageService.success(this.translate.instant('permit.config.editor.saved'));
                this.refreshService.notifyCategoryChanged(this.categoryId);
                this.saved.emit();
            },
            error: (err) => {
                this.saving = false;
                this.messageService.error(this.translate.instant(permitDepartmentErrorKey(err)));
            },
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
